import { FC, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';

import OperationMenuDots from 'modules/_shared/components/MenuDots/OperationMenuDots';
import Breadcrumb, {
  BreadcrumbItem,
} from 'modules/_shared/components/Breadcrumb';

import { TradingOfferOperationStatus } from 'types/TradingOfferOperation';
import { TradingOffer } from 'types/TradingOffer';

import {
  statusBadgeMap,
  menuStatusOptions,
} from '../constants/tradingOfferOperationStatus';
import getOfferReference from '../utils/getOfferReference';

type TradingOfferOperationDetailHeaderProps = {
  tradingOffer: TradingOffer | null;
  operationReference: string;
  operationStatus: TradingOfferOperationStatus;
  formattedRequestDate: string;
  sellerName: string;
  isAdmin: boolean;
  societyId: string;
  societyName: string;
  onUpdateStatus: (
    status: TradingOfferOperationStatus,
    comment?: string
  ) => void;
};

const TradingOfferOperationDetailHeader: FC<
  TradingOfferOperationDetailHeaderProps
> = ({
  tradingOffer,
  operationReference,
  operationStatus,
  formattedRequestDate,
  sellerName,
  isAdmin,
  societyId,
  societyName,
  onUpdateStatus,
}) => {
  const { t } = useTranslate();
  const location = useLocation();
  const partnerQuery = new URLSearchParams(location.search).get('partnerId');
  const pq = partnerQuery ? `?partnerId=${partnerQuery}` : '';

  const statusBadge =
    statusBadgeMap[operationStatus] || statusBadgeMap.PENDING;

  const offerRef = useMemo(
    () => getOfferReference(tradingOffer),
    [tradingOffer]
  );

  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { label: t('Dashboard'), to: '/' },
      { label: societyName, to: `/sociedad/${societyId}` },
      {
        label: t('EquityMarket'),
        to: `/mercado-de-acciones/${societyId}${pq}`,
      },
      {
        label: t('Offers'),
        to: `/ofertas/${societyId}${pq}`,
      },
      {
        label: offerRef,
        to: `/detalle-oferta/${societyId}/${tradingOffer?._id}${pq}`,
      },
      { label: operationReference, isActive: true },
    ],
    [t, societyName, societyId, offerRef, tradingOffer, operationReference, pq]
  );

  return (
    <>
      <div className="nk-block-head-content mb-3">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="nk-block-head page-header-subtitle">
        <div className="nk-block-between">
          <div className="nk-block-head-content">
            <h3 className="nk-block-title page-title">
              {tradingOffer?.type === 'SELL'
                ? t('PurchaseOperation')
                : t('SaleOperation')}
              <span className="badge badge-outline badge-primary ml-2">
                {operationReference}
              </span>
            </h3>
            <div className="nk-block-des text-soft">
              <p>
                {t('StartedOn')} {formattedRequestDate}
                {sellerName && (
                  <>
                    {' '}
                    · {t('Counterparty')}: <strong>{sellerName}</strong>
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="nk-block-head-content">
            <div className="toggle-wrap nk-block-tools-toggle">
              <div className="toggle-expand-content" data-content="pageMenu">
                <ul className="nk-block-tools g-3">
                  <li>
                    <span className={`badge ${statusBadge.className}`}>
                      <em className={`icon ni ${statusBadge.icon} mr-1`} />
                      <span>{t(statusBadge.label)}</span>
                    </span>
                  </li>

                  {isAdmin &&
                    operationStatus !== 'COMPLETED' &&
                    operationStatus !== 'CANCELLED' &&
                    operationStatus !== 'REJECTED' && (
                      <li>
                        {/* @ts-ignore */}
                        <OperationMenuDots
                          title={t('ManageOperation')}
                          variant="primary"
                          menuOptions={[
                            ...menuStatusOptions.map((status) => ({
                              icon: status.icon,
                              text: t(status.key),
                              action: () =>
                                onUpdateStatus(
                                  status.key as TradingOfferOperationStatus
                                ),
                              dropdown: true,
                            })),
                            {
                              icon: 'ni-cross-circle',
                              text: t('Rejected'),
                              action: () => onUpdateStatus('REJECTED'),
                              dropdown: true,
                              borderTop: true,
                            },
                            {
                              icon: 'ni-na',
                              text: t('Cancelled'),
                              action: () => onUpdateStatus('CANCELLED'),
                              dropdown: true,
                            },
                          ]}
                        />
                      </li>
                    )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TradingOfferOperationDetailHeader;
